import { useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"

import { supabase } from "@/lib/supabase"
import { buildSessionContentFields, resolveSessionAddress } from "@/types/sessionContentFields"

import { useSessions } from "./useSessions"

type RecurrencePattern = "none" | "daily" | "weekly" | "monthly"

// Map our internal OrientationEvent to/from the Session type
export interface OrientationEvent {
  id: string
  title: string
  jobPostings: string[]
  date: Date
  startTime: string
  endTime: string
  location: string
  locationAddress: string
  capacity: number
  registered: number
  depot_id?: string
  status: "Open" | "Closed" | "Deleted"
}

export interface NewOrientationEvent {
  title: string
  selectedJobPostings: string[]
  date: Date
  startTime: string
  endTime: string
  locationKey: string
  locationAddress: string
  capacity: number
  recurrence: RecurrencePattern
  recurrenceEndDate: Date | null
  status?: "Open" | "Closed" | "Deleted"
}

interface UseOrientationEventsProps {
  jobPostings: { value: string; label: string }[]
  locations: { value: string; label: string }[]
  filters?: {
    depot_id?: string
    status?: "Open" | "Closed" | "Deleted"
    date_from?: string
    date_to?: string
    search?: string
  }
}

export const useOrientationEvents = ({
  locations = [],
  filters = {},
}: UseOrientationEventsProps) => {
  // Initialize queryClient for cache invalidation
  const queryClient = useQueryClient()
  // Initialize with default form values
  const [newEvent, setNewEvent] = useState<NewOrientationEvent>({
    title: "",
    selectedJobPostings: [],
    date: new Date(),
    startTime: "10:00",
    endTime: "12:00",
    locationKey: "",
    locationAddress: "",
    capacity: 20,
    recurrence: "none",
    recurrenceEndDate: null,
    status: "Open",
  })

  // Use the sessions hook with a type filter for orientation
  const { sessions, fetchNextPage, hasNextPage, isFetchingNextPage, status, error, createSession } =
    useSessions({
      filters: {
        ...filters,
        type: "orientation", // Always filter for orientation type sessions
      },
    })

  // Handle adding/removing job postings selection
  const toggleJobPosting = (value: string) => {
    setNewEvent((prev) => {
      const currentPostings = [...prev.selectedJobPostings]
      const index = currentPostings.indexOf(value)

      if (index === -1) {
        // Add the job posting
        return { ...prev, selectedJobPostings: [...currentPostings, value] }
      } else {
        // Remove the job posting
        currentPostings.splice(index, 1)
        return { ...prev, selectedJobPostings: currentPostings }
      }
    })
  }

  // Map sessions to orientation events for UI
  const events: OrientationEvent[] = sessions.map((session) => {
    const locationObj = locations.find((loc) => loc.value === session.depot_id)

    return {
      id: session.id,
      title: session.title || "",
      jobPostings: session.jobs || [],
      date: new Date(session.date),
      startTime: format(new Date(`2025-01-01T${session.start_time}`), "h:mm a"),
      endTime: format(new Date(`2025-01-01T${session.end_time}`), "h:mm a"),
      location: locationObj ? locationObj.label : session.depot?.name || session.depot_id || "",
      locationAddress: resolveSessionAddress(session),
      capacity: session.capacity,
      registered: session.registered,
      depot_id: session.depot_id,
      status: session.status,
    }
  })

  const handleCreateEvent = async () => {
    if (!newEvent.locationKey || newEvent.selectedJobPostings.length === 0 || !newEvent.title)
      return

    // Get the formatted date as ISO string (YYYY-MM-DD)
    const formattedDate = format(newEvent.date, "yyyy-MM-dd")

    const contentFields = buildSessionContentFields({
      location_address: newEvent.locationAddress,
    })

    // If recurrence is none, create a single session
    if (newEvent.recurrence === "none") {
      // Create the session
      createSession.mutate({
        title: newEvent.title,
        date: formattedDate,
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        depot_id: newEvent.locationKey,
        capacity: newEvent.capacity,
        jobs: newEvent.selectedJobPostings, // Store job IDs/values
        status: newEvent.status || "Open",
        type: "orientation", // Set the type to orientation
        content_fields: contentFields,
      })
    } else {
      // Create multiple sessions based on recurrence pattern
      const sessions = []
      let currentDate = new Date(newEvent.date)
      const endDate = newEvent.recurrenceEndDate || new Date(currentDate)

      // Always create at least one session for the initial date
      sessions.push({
        date: format(currentDate, "yyyy-MM-dd"),
        start_time: newEvent.startTime,
        end_time: newEvent.endTime,
        depot_id: newEvent.locationKey,
        capacity: newEvent.capacity,
        jobs: newEvent.selectedJobPostings,
        status: newEvent.status || "Open",
        type: "orientation",
        title: newEvent.title,
        content_fields: contentFields,
      })

      // Advance to the next date based on recurrence pattern
      if (newEvent.recurrence === "daily") {
        currentDate.setDate(currentDate.getDate() + 1)
      } else if (newEvent.recurrence === "weekly") {
        currentDate.setDate(currentDate.getDate() + 7)
      } else if (newEvent.recurrence === "monthly") {
        currentDate.setMonth(currentDate.getMonth() + 1)
      } else {
        // If recurrence is "none", we don't need to create more sessions
        currentDate = new Date(endDate.getTime() + 1) // Set currentDate past endDate to exit loop
      }

      // Generate dates based on recurrence pattern until end date
      while (currentDate <= endDate) {
        // Add the session with the current date
        sessions.push({
          date: format(currentDate, "yyyy-MM-dd"),
          start_time: newEvent.startTime,
          end_time: newEvent.endTime,
          depot_id: newEvent.locationKey,
          capacity: newEvent.capacity,
          jobs: newEvent.selectedJobPostings,
          status: newEvent.status || "Open",
          type: "orientation",
          title: newEvent.title,
          content_fields: contentFields,
        })

        // Advance to next occurrence based on pattern
        if (newEvent.recurrence === "daily") {
          currentDate.setDate(currentDate.getDate() + 1)
        } else if (newEvent.recurrence === "weekly") {
          currentDate.setDate(currentDate.getDate() + 7)
        } else if (newEvent.recurrence === "monthly") {
          currentDate.setMonth(currentDate.getMonth() + 1)
        }
      }

      // Create all sessions
      for (const session of sessions) {
        createSession.mutate(session)
      }
    }

    // Reset the form
    setNewEvent({
      title: "",
      selectedJobPostings: [],
      date: new Date(),
      startTime: "10:00",
      endTime: "12:00",
      locationKey: "",
      locationAddress: "",
      capacity: 20,
      recurrence: "none",
      recurrenceEndDate: null,
      status: "Open",
    })
  }
  // Handle toggling event status
  const handleToggleStatus = async (eventId: string, currentStatus: string) => {
    try {
      // Determine the new status
      const newStatus = currentStatus === "Open" ? "Closed" : "Open"

      // Update the session status in the database
      const { error } = await supabase
        .from("sessions")
        .update({ status: newStatus })
        .eq("id", eventId)

      if (error) {
        console.error("Error updating session status:", error)
        return
      }

      // Refetch the sessions data
      await queryClient.invalidateQueries({
        queryKey: ["sessions"],
      })
    } catch (error) {
      console.error("Error in handleToggleStatus:", error)
    }
  }

  return {
    events,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
    newEvent,
    setNewEvent,
    toggleJobPosting,
    handleCreateEvent,
    handleToggleStatus,
    // Export mutation state for UI feedback
    createSession,
  }
}
